import { Trans, useLingui } from '@lingui/react/macro';
import { GetApiAuditLogResponse } from '@sit/openapi';
import PAGE_SIZES from '@web/constants/pageSizes';
import { dateWithFallback, stringWithFallback, timeWithFallback } from '@web/helpers/label';
import { useDateFormat } from '@web/services/locale';
import Pager from 'carbon-react/lib/components/pager';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'carbon-react/lib/components/table';
import Typography from 'carbon-react/lib/components/typography';
import { useId } from 'react';
import styled from 'styled-components';

const TableBox = styled.div`
  margin-bottom: ${(props) => props.theme.spacing * 3}px;
  width: 100%;
`;

const AuditPager = styled(Pager)`
  background: var(--colorsUtilityYang100);

  & [data-component='text-input'],
  & [data-component='simple-select'] {
    --fieldSpacing: 0px;
  }
`;

interface AuditTableProps {
  data: GetApiAuditLogResponse | undefined;
  loading?: boolean;
  page: number;
  pageSize: number;
  onPaginate: (currentPage: number, pageSize: number, origin: string) => void;
}

function useTableEntries(entries: GetApiAuditLogResponse['entries']) {
  const dateFormat = useDateFormat();
  const { t } = useLingui();

  const localizeAction = (value: string | undefined | null): string => {
    switch (value) {
      case 'create':
        return t`Created`;
      case 'update':
        return t`Updated`;
      default:
        return stringWithFallback(value);
    }
  };

  const localizeSource = (value: string | undefined | null): string => {
    switch (value) {
      case 'api':
        return t`API`;
      case 'ui':
        return t`UI`;
      case 'mobile':
        return t`Mobile`;
      default:
        return stringWithFallback(value);
    }
  };

  return entries.map((entry) => {
    const timestamp = new Date(entry.timestamp);

    return {
      ...entry,
      key: `${entry.timestamp}-${encodeURIComponent(entry.fieldName)}`,
      timestamp: `${dateWithFallback(timestamp, dateFormat)} ${timeWithFallback(timestamp)}`,
      action: localizeAction(entry.action),
      user: stringWithFallback(entry.user),
      source: localizeSource(entry.source),
    };
  });
}

function AuditTable({ data, loading = false, page, pageSize, onPaginate }: AuditTableProps) {
  const tableId = useId();
  const tableEntries = useTableEntries(data?.entries ?? []);
  return (
    <>
      <TableBox>
        <Table variant="subtle-white" maxWidth="100%">
          <TableHead>
            <TableRow id={`${tableId}-row-1`}>
              <TableHeader width="180px">
                <Trans>Date</Trans>
              </TableHeader>
              <TableHeader>
                <Trans>Action</Trans>
              </TableHeader>
              <TableHeader width="200px">
                <Trans>Completed by</Trans>
              </TableHeader>
              <TableHeader>
                <Trans>Source</Trans>
              </TableHeader>
              <TableHeader>
                <Trans>Field</Trans>
              </TableHeader>
              <TableHeader>
                <Trans>Before</Trans>
              </TableHeader>
              <TableHeader>
                <Trans>After</Trans>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableEntries.map((entry) => (
              <TableRow id={`${tableId}-${entry.key}`} key={entry.key}>
                <TableCell>
                  <Typography mb={0}>{entry.timestamp}</Typography>
                </TableCell>
                <TableCell>
                  <Typography mb={0}>{entry.action}</Typography>
                </TableCell>
                <TableCell>
                  <Typography mb={0}>{entry.user}</Typography>
                </TableCell>
                <TableCell>
                  <Typography mb={0}>{entry.source}</Typography>
                </TableCell>
                <TableCell>
                  <Typography mb={0}>{entry.fieldName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography whiteSpace="nowrap" mb={0}>
                    {entry.fieldBefore}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography whiteSpace="nowrap" mb={0}>
                    {entry.fieldAfter}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {tableEntries.length === 0 ? (
              loading ? (
                <TableRow id={`${tableId}-row-2`}>
                  <TableCell colSpan={7}>
                    <Typography textAlign="center" mb={0}>
                      <Trans>Loading...</Trans>
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow id={`${tableId}-row-3`}>
                  <TableCell colSpan={7}>
                    <Typography textAlign="center" mb={0}>
                      <Trans>No results</Trans>
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            ) : null}
          </TableBody>
        </Table>
      </TableBox>
      <AuditPager
        showTotalRecords
        totalRecords={data?.totalCount}
        showPageSizeSelection
        pageSize={pageSize}
        currentPage={page}
        onPagination={onPaginate}
        // @ts-expect-error it's OK that it's not mutable, the component does not mutate
        pageSizeSelectionOptions={PAGE_SIZES}
      />
    </>
  );
}

export default AuditTable;
